import { StyledButton } from 'components/button';
import { Label } from 'components/checkbox/checkbox.styles';
import { Link as DownloadLink } from 'components/download/download.styled';
import { H4, H5 } from 'components/headings';
import { Text } from 'components/ui/text';
import { hideVisually, rem } from 'polished';
import styled, { css } from 'styled-components';

const MAX_CONTENT_WIDTH = 650;
const OPTION_PADDING = rem(14);

export const Input = styled.input`
  ${hideVisually()}
`;

export const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 100%;
  padding: var(--header-height) ${rem(30)} ${rem(40)};
`;

interface QuestionWrapperProps {
  hasDescription?: boolean;
}

/**
 * Accessibility component.
 *
 * This component needs to wrap the
 * - Title
 * - Description
 * - Answer input fields
 *
 * In order for screen-readers to associate the title and description with the input fields.
 */
export const QuestionWrapper = styled.div.attrs(
  ({ hasDescription = true }: QuestionWrapperProps) => ({
    role: 'group',
    'aria-labelledby': 'question-title',
    'aria-describedby': hasDescription ? 'question-desc' : undefined,
  })
)<QuestionWrapperProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${rem(10)} 0 0;
`;

export const Title = styled.h1.attrs(() => ({
  id: 'question-title',
}))`
  margin: ${rem(20)} 0 0;
  font-size: ${rem(24)};
  line-height: 1.15;
  text-align: center;

  ${({ theme }) => theme.mediaQueries.medium} {
    margin-top: ${rem(50)};
  }
`;

export const Description = styled.p.attrs(() => ({
  id: 'question-desc',
}))`
  max-width: ${rem(255)};
  margin: ${rem(20)} 0 0;
  font-size: ${rem(20)};
  line-height: 1.3;
  text-align: center;

  ${({ theme }) => theme.mediaQueries.medium} {
    max-width: ${rem(520)};
  }
`;

export const AlertWrapper = styled.div`
  width: 100%;
  max-width: ${rem(MAX_CONTENT_WIDTH)};
  margin: ${rem(30)} auto 0;

  > * + * {
    margin-top: ${rem(30)};
  }
`;

export const ProgressWrapper = styled.div`
  width: 100%;
  max-width: ${rem(320)};
  margin: 0 auto;
`;

interface AnswerListProps {
  colCount?: 2;
}

export const AnswerList = styled.ol<AnswerListProps>`
  width: 100%;
  max-width: ${({ colCount }) =>
    colCount === 2 ? rem(MAX_CONTENT_WIDTH) : rem(322)};
  margin: ${rem(20)} 0 0;
  padding: 0;
  list-style: none;

  ${({ theme }) => theme.mediaQueries.medium} {
    margin-top: ${rem(40)};
    columns: ${({ colCount }) => colCount};
    column-gap: ${rem(10)};
  }
`;

export const Answer = styled.li`
  & + & {
    margin: 0;
  }
`;

export const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  width: 100%;
  max-width: ${rem(MAX_CONTENT_WIDTH)};
  margin: ${rem(30)} auto 0;

  ${({ theme }) => theme.mediaQueries.medium} {
    margin-top: ${rem(40)};
  }

  ${StyledButton} {
    width: calc(50% - ${rem(6)});

    &:only-child {
      /* auto margins will override 'justify-content' to center a single button */
      margin-right: auto;
      margin-left: auto;
    }

    ${({ theme }) => theme.mediaQueries.small} {
      width: unset;
    }

    ${({ theme }) => theme.mediaQueries.smallMax} {
      width: calc(100% - ${rem(6)});
    }
  }
`;

export const FileWrapper = styled.div`
  width: 100%;
  max-width: ${rem(650)};
  margin: ${rem(30)} auto;

  ${DownloadLink} {
    margin-right: 0;
    margin-left: 0;
  }
`;

export const InputWrapper = styled.div`
  position: relative;
  margin-bottom: ${rem(12)};
`;

export const TextFieldWrapper = styled.div`
  width: 100%;
  max-width: ${rem(650)};
  margin: ${rem(30)} auto 0;
  padding: ${rem(42)} ${rem(38)};
  color: ${({ theme }) => theme.colors.palette.black};
  font-size: ${rem(16)};
  background-color: ${({ theme }) => theme.colors.palette.white};
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.medium};

  ${({ theme }) => theme.mediaQueries.medium} {
    padding: ${rem(60)} ${rem(70)} ${rem(75)};
  }

  ${({ theme }) => theme.mediaQueries.smallMax} {
    padding: 1em;
  }
`;

export const CheckboxFieldWrapper = styled(TextFieldWrapper)`
  ${Text} {
    margin-top: 0;
    margin-bottom: 0;
  }

  .marginTop {
    margin-top: 30px;
  }

  ${Label} ${Input} {
    height: 50px;
  }
`;

export const TooltipDialogWrapper = styled.div`
  margin-top: 30px;
`;

export const CardInnerWrapper = styled.div`
  margin: 10px;
`;

export const CardOuterWrapper = styled(TextFieldWrapper)`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.mediaQueries.medium} {
    padding: ${rem(30)} ${rem(40)} ${rem(45)};
  }

  svg {
    align-self: flex-end;
    width: 24px;
    height: 24px;
  }
`;

const CommonInputStyles = css`
  width: 100%;
  margin: ${rem(3)} 0 ${rem(20)};
  padding: ${rem(10)} ${OPTION_PADDING};
  border: 1px solid ${({ theme }) => theme.colors.palette.grey};
  border-radius: ${({ theme }) => theme.borderRadius.small};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const TextField = styled.textarea`
  resize: vertical;
  ${CommonInputStyles}
`;

export const Fieldset = styled.fieldset`
  max-width: ${rem(330)};
  margin: ${rem(60)} auto 0;
  padding: 0;
  border: 0;

  ${({ theme }) => theme.mediaQueries.medium} {
    margin-top: ${rem(70)};
  }

  &:first-child {
    margin-top: 0;
  }

  legend {
    margin-bottom: 1em;
  }

  label {
    display: block;
  }

  input {
    ${CommonInputStyles}
  }
`;

export const IntroCopy = styled.div`
  margin-bottom: ${rem(40)};
  line-height: 1.5;

  > *:first-child {
    margin-top: 0;
  }

  &:only-child {
    margin-bottom: 0;
  }

  p {
    margin-top: 0;

    & + p {
      margin-top: ${rem(20)};
    }
  }

  /* a h2 using h4 style */
  ${H4} ~ ${H4} {
    margin-top: 2em;
  }

  /* a h3 using h5 style */
  ${H5} {
    margin-top: 1.5em;
    margin-bottom: 0;
  }

  > ul {
    margin-top: 0.3em;
    padding-left: ${rem(20)};
  }

  li + li {
    margin-top: 0.5em;
  }

  ${({ theme }) => theme.mediaQueries.medium} {
    > ul {
      margin-top: ${rem(20)};
      margin-bottom: 0;
    }

    ${H4} ~ ${H4} {
      margin-bottom: 1em;
    }
  }
`;

export const CheckboxWrapper = styled.div`
  margin: 0 ${rem(-10)} ${rem(-20)};
  padding: ${rem(20)} ${rem(5)} 0;
  border-top: 1px solid ${({ theme }) => theme.colors.palette.grey};

  ${({ theme }) => theme.mediaQueries.medium} {
    margin: 0 ${rem(-20)} ${rem(-30)};
    padding: ${rem(20)} ${rem(20)} 0;
  }
`;

export const CardWrapper = styled.div`
  width: 100%;
  max-width: ${rem(MAX_CONTENT_WIDTH)};
  margin: ${rem(30)} auto 0;
`;

export const CardTitleWrapper = styled.div`
  display: flex;
  svg {
    flex-shrink: 0;
    align-self: flex-start;
    width: 24px;
    height: 24px;
    margin-right: 10px;
  }
`;

export const ButtonIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  margin-right: 10px;
`;

interface IconButtonProps {
  isInline?: boolean;
}

export const IconButton = styled.button<IconButtonProps>`
  position: ${({ isInline }) => (isInline ? 'absolute' : 'relative')};
  align-self: flex-end;
  width: 24px;
  height: 24px;
  background: none;
  border: 0;
  cursor: pointer;

  ${({ isInline }) => {
    if (isInline) {
      return css`
        right: ${rem(12)};
        bottom: ${rem(12)};
        z-index: ${({ theme }) => theme.zIndex[1]};
        width: 18px;
        height: 18px;
      `;
    }
  }};

  svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    &:hover {
      color: ${({ theme }) => theme.colors.palette.redDark};
    }
  }
`;

export const PlanTitle = styled(Text)`
  color: ${({ theme }) => theme.components.text.text};
  font-weight: bold;
  text-align: center;
`;

export const TextInputWrapper = styled.div`
  margin-top: 20px;
`;

interface DialogLinkProps {
  inLine?: boolean;
}

export const DialogLink = styled.a<DialogLinkProps>`
  color: ${({ inLine, theme }) =>
    inLine ? theme.colors.palette.white : theme.colors.palette.black};
  font-weight: ${({ inLine }) => (inLine ? 600 : 'normal')};

  & + & div {
    padding-top: 10px;
  }

  span {
    text-decoration: underline;
  }

  svg {
    margin: 0 6px;
  }
`;

export const ButtonContentWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;

  &:hover {
    text-decoration: initial;
  }

  svg {
    flex-shrink: 0;
    width: ${rem(20)};
    height: ${rem(20)};
    margin-right: 12px;
  }
`;
